import { useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { QrBarcodeBlock } from '@lighthouse/block/src/blocks'
import { EVENT_TYPE, type QrBarcodeBlockAbstract, type VariableADTvalue } from '@lighthouse/core'
import { fieldConvertValue, generateText, getWithScopeIdAndRecordIdAndColumnId, useActionRunningLoadings } from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

interface QrBarcodeBlockControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: QrBarcodeBlockAbstract
}

const QrBarcodeBlockController: React.FC<QrBarcodeBlockControllerProps> = ({ blockData, ...rest }) => {
    const { id, config } = blockData
    const { content, eventList } = config
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id), [id, scope, record?.id])
    const { handleActionTriggerWithLoading } = useActionRunningLoadings()
    const { handleActionListTrigger } = useActionTrigger(id)
    const parseVariable = useParseVariableWithSubscription(content.value)

    const parseVariable2String = useCallback(
        (value: VariableADTvalue) => {
            const field = parseVariable(value, {
                viewRecord: record
            })
            if (!field) {
                return ''
            }

            return fieldConvertValue(field)
        },
        [parseVariable, record]
    )

    const handleClickEvent = useCallback(() => {
        const clickEvent = eventList?.find(ev => ev.type === EVENT_TYPE.CLICK)
        if (!clickEvent) {
            return
        }
        return handleActionTriggerWithLoading({
            id: uniqueId,
            actorEvent: clickEvent,
            trigger: (id, actorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record })
        })
    }, [eventList, handleActionListTrigger, handleActionTriggerWithLoading, record, uniqueId])

    const value = useMemo(
        () =>
            generateText(content.value, {
                variable: {
                    renderLabel: v => parseVariable2String(v.attrs.value)
                }
            }),
        [content.value, parseVariable2String]
    )

    return <QrBarcodeBlock blockData={blockData} code={value} onClick={handleClickEvent} {...rest} />
}

export default QrBarcodeBlockController
